
import React, { useMemo } from "react";
import {
  Line,
  LineChart as RechartsLineChart,
  ResponsiveContainer,
  XAxis,
  YAxis,
  Tooltip,
  TooltipProps,
  CartesianGrid,
  Legend,
  LegendProps,
  Area,
} from "recharts";

interface LineChartProps {
  data: any[];
  index: string;
  categories: string[];
  colors?: string[];
  valueFormatter?: (value: number) => string;
  startEndOnly?: boolean;
  showXAxis?: boolean;
  showYAxis?: boolean;
  showLegend?: boolean;
  showGradient?: boolean;
  yAxisWidth?: number;
  className?: string;
}

export function LineChart({
  data,
  index,
  categories,
  colors = ["hsl(var(--primary))", "#2563eb", "#16a34a", "#d97706", "#dc2626"],
  valueFormatter = (value: number) => value.toString(),
  startEndOnly = false,
  showXAxis = true,
  showYAxis = true,
  showLegend = true,
  showGradient = false,
  yAxisWidth = 56,
  className,
}: LineChartProps) {
  // Format the data for display
  const formattedData = useMemo(() => {
    return data.map((item) => ({
      ...item,
    }));
  }, [data]);

  // Custom tooltip component
  const CustomTooltip = (props: TooltipProps<any, any>) => {
    const { active, payload } = props;

    if (!active || !payload) return null;

    return (
      <div className="bg-background border rounded-md shadow-md p-2 text-sm">
        <div className="font-medium mb-1">{payload[0]?.payload[index]}</div>
        {payload.map((entry, i) => (
          <div key={i} className="flex items-center gap-2">
            <div
              className="w-3 h-3 rounded-full"
              style={{ backgroundColor: entry.color }}
            />
            <span className="text-muted-foreground">
              {entry.name}: {valueFormatter(entry.value)}
            </span>
          </div>
        ))}
      </div>
    );
  };

  // Custom legend
  const renderLegend = (props: LegendProps) => {
    const { payload } = props;

    return (
      <div className="flex justify-center gap-6 text-sm">
        {payload?.map((entry, i) => (
          <div key={`legend-${i}`} className="flex items-center gap-2">
            <div
              className="w-3 h-3 rounded-full"
              style={{ backgroundColor: entry.color }}
            />
            <span>{entry.value}</span>
          </div>
        ))}
      </div>
    );
  };
  
  // Format tick values for the x-axis
  const formatXAxisTick = (value: any, index: number) => {
    if (startEndOnly) {
      // Only show first and last labels
      if (index === 0 || index === data.length - 1) {
        return value;
      }
      return "";
    }
    return value;
  };

  return (
    <div className={className}>
      <ResponsiveContainer width="100%" height="100%">
        <RechartsLineChart
          data={formattedData}
          margin={{ top: 10, right: 10, left: 0, bottom: 30 }}
        >
          <CartesianGrid vertical={false} strokeDasharray="3 3" stroke="hsl(var(--muted))" opacity={0.3} />
          
          {showXAxis && (
            <XAxis
              dataKey={index}
              tick={{ fontSize: 12, fill: "hsl(var(--muted-foreground))" }}
              tickLine={false}
              axisLine={{ stroke: "hsl(var(--border))" }}
              tickFormatter={formatXAxisTick}
              tickMargin={10}
            />
          )}
          
          {showYAxis && (
            <YAxis
              width={yAxisWidth}
              tick={{ fontSize: 12, fill: "hsl(var(--muted-foreground))" }}
              tickLine={false}
              axisLine={false}
              tickFormatter={(value) => valueFormatter(value)}
            />
          )}
          
          <Tooltip content={<CustomTooltip />} />
          
          {showLegend && <Legend content={renderLegend} />}
          
          {categories.map((category, i) => (
            <Line
              key={category}
              type="monotone"
              dataKey={category}
              name={category}
              stroke={colors[i % colors.length]}
              strokeWidth={2}
              dot={{ r: 4, fill: colors[i % colors.length], stroke: colors[i % colors.length] }}
              activeDot={{ r: 6, fill: colors[i % colors.length], stroke: "hsl(var(--background))" }}
            >
              {showGradient && (
                <defs>
                  <linearGradient id={`gradient-${category}`} x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor={colors[i % colors.length]} stopOpacity={0.3} />
                    <stop offset="95%" stopColor={colors[i % colors.length]} stopOpacity={0} />
                  </linearGradient>
                </defs>
              )}
            </Line>
          ))}

          {/* Add gradient areas separately if needed */}
          {showGradient && categories.map((category, i) => (
            <Area
              key={`area-${category}`}
              type="monotone"
              dataKey={category}
              fill={`url(#gradient-${category})`}
              stroke="none"
              fillOpacity={1}
            />
          ))}
        </RechartsLineChart>
      </ResponsiveContainer>
    </div>
  );
}
